import { NetworkStatus, useQuery } from '@apollo/client';
import { Box, Heading, IconSize, Spinner, SpinnerSize, Text, TextFieldSize, useModalState } from '@bechtel/gds';
import { useAppUser, useDebounce } from '@bechtel/ht-clients-common';
import { FilterConditionInput, FilterInput, OperatorType, SearchInput, User } from '@bechtel/ht-types';
import { find, get, map } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { ErrorRetry } from '~/components';
import { GET_TOTAL_USER_COUNT } from '~/gql/user';
import { useSearchUser } from '~/hooks/use-search-user';
import { ProjectAdminMenuItem, ProjectAdminMenuType } from '../../list/types';
import { ProjectAdminMenuItems } from '../../list/utils';
import {
  ButtonBlock,
  ListStyled,
  NavLinkStyled,
  SearchFieldComponentStyled,
  SearchFieldStyled,
  UserListContainerStyled,
} from './styled';
import UserCard from './user-card';

export const UserItemList: FC = () => {
  const { appUser } = useAppUser();
  const { toggle, visible } = useModalState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceSearchQuery = useDebounce(searchQuery, 500);
  const { searchUser, loading, error, data, refetch, networkStatus, fetchMore, called } = useSearchUser();

  const usersLimit = 20;
  const offset = 0;
  const filterInput: FilterInput = useMemo(() => {
    const and: FilterConditionInput[] = [
      { field: 'userProjects.projectId', operator: OperatorType.EQUAL, value: appUser?.defaultProject?.id },
    ];
    const or: FilterConditionInput[] = [];
    if (debounceSearchQuery.length >= 2) {
      or.push({ field: 'firstName', operator: OperatorType.LIKE, value: debounceSearchQuery });
      or.push({ field: 'lastName', operator: OperatorType.LIKE, value: debounceSearchQuery });
      or.push({ field: 'bun', operator: OperatorType.LIKE, value: debounceSearchQuery });
    }
    const filter: FilterInput = {
      or: or,
      and: and,
    };
    return { ...filter };
  }, [debounceSearchQuery]);
  const {
    loading: totalLoading,
    error: totalError,
    data: totalData,
    refetch: refetchCount,
  } = useQuery<{ userCountForSearch: number }, { searchInput: SearchInput }>(GET_TOTAL_USER_COUNT, {
    variables: {
      searchInput: {
        sortInput: [],
      },
    },
  });
  useEffect(() => {
    if (!appUser?.defaultProject?.id) return;
    refetchCount();
  }, [appUser?.defaultProject?.id, refetchCount]);

  const totalRecords = totalData?.usersCountForSearch;
  useEffect(() => {
    if (debounceSearchQuery.length > 0 && debounceSearchQuery.length < 3) return;
    searchUser({
      filterInput: filterInput,
      sortInput: [],
      paginationInput: {
        limit: usersLimit,
        offset: offset,
      },
    });
  }, [debounceSearchQuery, searchUser]);

  const userList: User[] = get(data, 'searchUsers.items', []);
  const handleScroll = (e) => {
    if (!totalRecords) return;

    if (
      e.target.scrollTop + e.target.clientHeight + 1 >= e.target.scrollHeight &&
      !loading &&
      userList &&
      userList.length < totalRecords
    ) {
      fetchMore({
        variables: {
          searchInput: {
            filterInput: filterInput,
            paginationInput: {
              limit: usersLimit,
              offset: userList.length,
            },
            sortInput: [],
          },
        },
      });
    }
  };
  const prjUserAdminMenuItem = find(ProjectAdminMenuItems(), (item: ProjectAdminMenuItem) => {
    return item.type == ProjectAdminMenuType.USER_ADMIN;
  });
  //TODO waiting for api that return total user count
  return (
    <>
      <UserListContainerStyled isDrawerOpen={visible}>
        <Box
          pt={{ sm: '450' }}
          p={{ sm: '550' }}
          justifyContent={{ sm: 'space-between' }}
          alignItems={{ sm: 'center' }}
        >
          <Text size="600" family="openSans" weight="600">
            {totalRecords} {`Total Users`}
          </Text>
          <ButtonBlock to={`/project-admin/${prjUserAdminMenuItem?.value}/user/create`}>
            <Text size="400" weight="600" color="primary.700" style={{ lineHeight: '1.29' }}>
              + New User
            </Text>
          </ButtonBlock>
        </Box>
        <SearchFieldStyled>
          <SearchFieldComponentStyled
            size={TextFieldSize.LARGE}
            iconSize={IconSize.MEDIUM}
            placeholder={`Search by name or BUN...`}
            type="search"
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            value={searchQuery}
          />
          <Box
            ml={{ sm: '450' }}
            css={`
              max-width: ${({ theme }) => theme.spacings[800]};
            `}
          >
            {/* No Functionality implemented yet.So commented it now <SearchCanvasButtonStyled
              isDefaultFilterChange={true}
              onClick={toggle}
              appearance={ButtonAppearance.SECONDARY}
              size={ButtonSize.LARGE}
            >
               <StyledIcon icon="sliders" size={IconSize.LARGE} fillColor="primary.700" />
            </SearchCanvasButtonStyled> */}
          </Box>
        </SearchFieldStyled>

        <ListStyled
          onScroll={(e) => {
            handleScroll(e);
          }}
        >
          {map(userList, (userItem: User) => {
            const encodedId = encodeURIComponent(userItem.id);

            return (
              <NavLinkStyled
                data-testid={`user-list-item-${encodedId}`}
                to={`/project-admin/${prjUserAdminMenuItem?.value}/${encodedId}`}
                key={encodedId}
              >
                <UserCard key={userItem.id} userItem={userItem} />
              </NavLinkStyled>
            );
          })}
          {!loading && error && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              flexDirection={{ sm: 'column' }}
              p={{ sm: '500' }}
              data-testid="user-list-retry"
            >
              <ErrorRetry refetch={refetch}>
                There was a problem loading users. Please click the button below to try again.
              </ErrorRetry>
            </Box>
          )}
          {!loading && !error && userList?.length === 0 && (
            <Box
              flexDirection={{ sm: 'column' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              style={{ width: '100%', height: '100%' }}
              p={{ sm: '500' }}
              data-testid="user-list-empty"
            >
              <Heading size="400" weight="600" color="grey.600">
                No users found.
              </Heading>
            </Box>
          )}
          {(!called || networkStatus === NetworkStatus.refetch || loading) && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              p={{ sm: '500' }}
              data-testid="user-list-loading"
            >
              <Spinner size={SpinnerSize.LARGE} />
            </Box>
          )}
        </ListStyled>
      </UserListContainerStyled>
    </>
  );
};
